import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import hero from "@/assets/smart-home.webp";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Castle Rock";
const SLUG = "castle-rock";
const ZIP = "80108";
const URL = "https://apexav.com/castle-rock/smart-home-installation";

const faqs = [
  {
    q: "How much does a Castle Rock smart home installation cost?",
    a: "Most Castle Rock smart home projects land between $25,000 and $150,000 depending on home size and scope. A whole-home automation retrofit on a 4,000 sq ft Meadows home typically runs $35K–$60K. A new-build pre-wire and full integration on a Castle Pines Village estate runs $100K+. Every project is fixed-bid before any work starts.",
  },
  {
    q: "Should I pre-wire during new construction in Castle Rock?",
    a: "Absolutely — pre-wiring during framing in The Meadows, Cobblestone Ranch, Crystal Valley, or any new build is the single biggest cost-saver. We run Cat6A, fiber, speaker wire, conduit, and structured cabling before drywall closes up the walls. The marginal cost during framing is a fraction of what a retrofit costs later.",
  },
  {
    q: "Can you retrofit my existing Castle Rock home without tearing up walls?",
    a: "Yes. We've retrofitted hundreds of homes in The Meadows, Founders Village, Castlewood Ranch, and Sapphire Pointe with no drywall damage. Modern wireless platforms plus careful fishing of low-voltage cable through existing chases means most retrofits finish with zero patching.",
  },
  {
    q: "Which control platform do you recommend for Castle Rock homes?",
    a: "It depends on scope. For lighting-only or smaller homes we lean toward leading wireless retrofit platforms. For full whole-home integration (lighting, shades, audio, video, climate, security) we usually spec industry-leading control systems with strong dealer networks in Colorado. For ultra-high-end Castle Pines Village estates we often go with fully custom control platforms. We'll recommend the right platform during the free consultation.",
  },
  {
    q: "Do you handle Castle Pines Village HOA approvals?",
    a: "Yes. We're familiar with the Castle Pines Village Homes Association architectural review process and submit drawings and equipment specs on your behalf. Same for The Meadows Master Association.",
  },
];

const testimonials = [
  { quote: "Apex pre-wired our new Meadows home during framing and integrated everything — lights, shades, audio, security — into one unified app. Worth every penny.", name: "Mark D.", location: "The Meadows, Castle Rock" },
  { quote: "Full custom integration on our Castle Pines Village estate. They worked around our existing finishes and the install was completely invisible.", name: "Linda P.", location: "Castle Pines Village" },
  { quote: "Smart lighting and Sonos audio across our Cobblestone Ranch home. Clean install, zero drywall damage, and the app is so much easier than what we had before.", name: "Chris W.", location: "Cobblestone Ranch" },
];

export const Route = createFileRoute("/castle-rock/smart-home-installation")({
  head: () => ({
    meta: [
      { title: "Smart Home Installation Castle Rock | Apex AV Denver" },
      { name: "description", content: "Custom smart home design & installation in Castle Rock, The Meadows, and Crystal Valley. Certified integrator. Free in-home consultation." },
      { name: "keywords", content: "smart home installation Castle Rock, home automation Castle Rock, smart home pre-wire Castle Rock, The Meadows smart home, smart home company Castle Rock" },
      { property: "og:title", content: "Smart Home Installation Castle Rock — Apex AV" },
      { property: "og:description", content: "Custom smart home design & installation in Castle Rock & Castle Pines Village." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Smart Home Installation Castle Rock — Apex AV" },
      { name: "twitter:description", content: "Custom smart home design & installation in Castle Rock & Castle Pines Village." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Smart Home Installation in ${NEIGHBORHOOD}`,
              description: `Custom smart home design and installation in ${NEIGHBORHOOD}, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, CO ${ZIP}` },
              serviceType: "Smart Home Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "25000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 15000,
              serviceName: "Smart Home Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="smart home installation"
      serviceKey="smart-home"
      parentServiceLabel="smart home"
      parentServiceTo="/services/smart-home"
      h1="Smart Home Installation in Castle Rock, CO"
      intro="Whole-home automation integration for Castle Rock new builds, retrofits, and Castle Pines Village estates. Lighting, shades, audio, video, climate, security, and networking — all on one app, with one phone number to call when you have a question."
      hero={hero}
      bullets={[
        "Certified smart home integrator",
        "New-construction pre-wire coordination",
        "Whole-home lighting & motorized shades",
        "Multi-room audio & video distribution",
        "Enterprise-grade WiFi (Ruckus / Araknis)",
        "24/7 remote monitoring & support",
      ]}
      microNeighborhoods={["Castle Pines Village", "The Meadows", "Cobblestone Ranch", "Crystal Valley", "Keene Ranch", "Founders Village", "Castlewood Ranch", "Sapphire Pointe"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "Designed for Castle Rock new builds and retrofits",
          body: (
            <>
              <p>
                Castle Rock is one of the fastest-growing markets in Colorado, and we work both ends of it — pre-wiring new builds in The Meadows, Cobblestone Ranch, Crystal Valley, and Sapphire Pointe during framing, and retrofitting existing homes in Founders Village, Castlewood Ranch, and Castle Pines Village without tearing up drywall. The right approach depends on where you are in the construction timeline.
              </p>
              <p>
                Every project starts with a free in-home or pre-construction walkthrough. We map out lighting scenes, audio zones, video sources, network coverage, security cameras, and climate zones, then deliver a fixed-scope design before any work begins.
              </p>
            </>
          ),
        },
        {
          heading: "What a Castle Rock smart home includes",
          body: (
            <>
              <p>
                A complete Castle Rock install typically covers <strong>whole-home lighting control</strong>, <strong>motorized shades</strong> on south- and west-facing windows for Colorado's intense sun, <strong>multi-room audio</strong> (Sonos or Sonance), <strong>video distribution</strong> to every TV, <strong>enterprise-grade WiFi</strong> (Araknis access points sized for 4,000–10,000 sq ft homes), <strong>4K security cameras</strong>, <strong>climate integration</strong>, and a <strong>central control processor</strong> tying it all together.
              </p>
              <p>
                One app. One remote. One scene that says "Goodnight" — lights off, shades down, doors locked, alarm armed, thermostat set back. That's the standard we deliver in every Castle Rock home.
              </p>
            </>
          ),
        },
        {
          heading: "Pair it with a dedicated theater",
          body: (
            <>
              <p>
                Most Castle Rock smart home projects we do include a dedicated theater or media room as part of the same scope. See our <a href="/castle-rock/home-theater-installation" className="text-brand-text underline underline-offset-2">Castle Rock home theater installation</a> page for the full theater scope, or browse all <a href="/services" className="text-brand-text underline underline-offset-2">Apex AV services</a>.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
